// ========================================
// UTILIDAD DE SUBIDA DIRECTA A S3 - DOCUMENTOS EXTERNOS
// Mismo flujo que Fordata (campo tipo archivo) y Documentos:
//   presign -> PUT del navegador a S3 -> confirm
// El archivo nunca pasa por el dyno; en BD solo queda el filename, que es la
// referencia con la que CarrierWave reconstruye la URL.
// Límite: 100MB
// ========================================

export const MAX_EXTERNAL_DOCUMENT_SIZE = 104857600; // 100MB en bytes

const csrfToken = () => document.querySelector('[name="csrf-token"]').content;

/**
 * Valida el archivo antes de subirlo
 * @param {File} file - Archivo a validar
 * @returns {Object} {isValid, error}
 */
export function validateExternalDocumentForS3Upload(file) {
    if (!file || !file.name) {
        return { isValid: false, error: 'No se ha seleccionado ningún archivo' };
    }

    if (file.size > MAX_EXTERNAL_DOCUMENT_SIZE) {
        return {
            isValid: false,
            error: `El archivo excede el límite de 100 MB permitido (tamaño actual: ${(file.size / 1024 / 1024).toFixed(2)}MB)`
        };
    }

    return { isValid: true, error: null };
}

/**
 * Sube el documento externo directamente a S3 usando presigned URLs
 * @param {File} file - Archivo a subir
 * @param {Object} params - {externalDocumentId} (opcional: solo en edición)
 * @returns {Promise<Object>} Resultado de la subida
 */
export async function uploadExternalDocumentDirectlyToS3(file, params = {}) {
    const { externalDocumentId } = params;

    try {
        // PASO 1: Solicitar URL firmada al backend
        const requestBody = {
            file_name: file.name,
            content_type: file.type,
            file_size: file.size
        };

        // Sin id el archivo va a la carpeta temporal y el create lo mueve.
        if (externalDocumentId) requestBody.external_document_id = externalDocumentId;

        const urlResponse = await fetch('/document_management/generate_external_document_upload_url', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken()
            },
            body: JSON.stringify(requestBody)
        });

        if (!urlResponse.ok) {
            const errorData = await urlResponse.json().catch(() => ({}));
            throw new Error(errorData.error || 'Error obteniendo URL firmada');
        }

        const { upload_url, file_key, external_document_id } = await urlResponse.json();

        // PASO 2: Subir el archivo directamente a S3
        const uploadResponse = await fetch(upload_url, {
            method: 'PUT',
            body: file,
            headers: {
                'Content-Type': file.type,
                'Content-Length': file.size.toString()
            }
        });

        if (!uploadResponse.ok) {
            throw new Error(`Error subiendo a S3: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        // PASO 3: Confirmar la subida en el backend
        const confirmBody = { file_key: file_key };
        if (external_document_id) confirmBody.external_document_id = external_document_id;

        const confirmResponse = await fetch('/document_management/confirm_external_document_upload', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken()
            },
            body: JSON.stringify(confirmBody)
        });

        if (!confirmResponse.ok) {
            const errorData = await confirmResponse.json().catch(() => ({}));
            throw new Error(errorData.error || 'Error confirmando subida');
        }

        const confirmData = await confirmResponse.json();

        return {
            success: true,
            file_url: confirmData.file_url,
            file_key: confirmData.file_key,
            external_document_id: confirmData.external_document_id,
            file_name: file.name,
            file_size: file.size,
            message: 'Archivo subido exitosamente'
        };

    } catch (error) {
        console.error('❌ [DOC. EXTERNOS] Error en subida directa a S3:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Subida con reintentos y callback de progreso
 * @param {File} file - Archivo a subir
 * @param {Object} params - {externalDocumentId}
 * @param {Function} onProgress - Callback opcional de progreso
 * @returns {Promise<Object>} Resultado
 */
export async function uploadExternalDocumentWithProgress(file, params = {}, onProgress = null) {
    const maxRetries = 3;
    let attempt = 0;
    let lastError = 'Error subiendo archivo';

    while (attempt < maxRetries) {
        if (onProgress) {
            onProgress({ phase: 'preparing', progress: 0, message: 'Preparando subida...' });
        }

        const result = await uploadExternalDocumentDirectlyToS3(file, params);

        if (result.success) {
            if (onProgress) {
                onProgress({ phase: 'completed', progress: 100, message: '¡Archivo subido exitosamente!' });
            }
            return result;
        }

        lastError = result.error;
        attempt++;
        console.warn(`⚠️  [DOC. EXTERNOS] Intento ${attempt} de ${maxRetries} falló:`, result.error);

        if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
            if (onProgress) {
                onProgress({ phase: 'retrying', progress: 0, message: `Reintentando subida... (${attempt}/${maxRetries})` });
            }
        }
    }

    if (onProgress) {
        onProgress({ phase: 'failed', progress: 0, message: 'Error subiendo archivo' });
    }

    return { success: false, error: lastError };
}

/**
 * Elimina un archivo de S3
 * @param {string} fileUrl - URL del archivo en S3
 * @returns {Promise<Object>} Resultado
 */
export async function deleteExternalDocumentFromS3(fileUrl) {
    try {
        const response = await fetch('/document_management/delete_external_document_s3_file', {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': csrfToken()
            },
            body: JSON.stringify({ file_url: fileUrl })
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Error eliminando archivo');
        }

        return { success: true };

    } catch (error) {
        console.error('❌ [DOC. EXTERNOS] Error eliminando archivo de S3:', error);
        return { success: false, error: error.message };
    }
}

/**
 * Comprime (solo imágenes) y sube el documento externo a S3.
 * @param {File} originalFile - Archivo original del usuario
 * @param {Object} params - {externalDocumentId}
 * @param {Function} onProgress - Callback opcional de progreso
 * @returns {Promise<Object>} Resultado
 */
export async function compressAndUploadExternalDocument(originalFile, params = {}, onProgress = null) {
    let fileToUpload = originalFile;

    if (originalFile.type && originalFile.type.startsWith('image/')) {
        if (onProgress) {
            onProgress({ phase: 'compressing', progress: 25, message: '🗜️ Optimizando imagen...' });
        }

        try {
            const { compressFileForBeewo } = await import('./imageCompression.js');
            fileToUpload = await compressFileForBeewo(originalFile);
        } catch (compressionError) {
            console.warn('⚠️  [DOC. EXTERNOS] Error en compresión, usando archivo original:', compressionError);
        }
    }

    // Validar el tamaño DESPUÉS de comprimir
    const validation = validateExternalDocumentForS3Upload(fileToUpload);
    if (!validation.isValid) {
        return { success: false, error: validation.error };
    }

    if (onProgress) {
        onProgress({ phase: 'uploading', progress: 50, message: '☁️ Subiendo a la nube...' });
    }

    return uploadExternalDocumentWithProgress(
        fileToUpload,
        params,
        (uploadProgress) => {
            if (onProgress) {
                onProgress({
                    phase: 'uploading',
                    progress: 50 + (uploadProgress.progress * 0.5),
                    message: uploadProgress.message
                });
            }
        }
    );
}

export default {
    MAX_EXTERNAL_DOCUMENT_SIZE,
    validateExternalDocumentForS3Upload,
    uploadExternalDocumentDirectlyToS3,
    uploadExternalDocumentWithProgress,
    deleteExternalDocumentFromS3,
    compressAndUploadExternalDocument
};
